import { Link, useLocation, useNavigate } from "react-router-dom";
import { Shield, Mic, BarChart3, Swords, Headphones, Dumbbell, FileText, Map, User, LogOut, Users, Settings, Crown, Radio, Landmark, BrainCircuit, ShieldCheck, Grid, Sparkles, LayoutDashboard, Menu, X, MessageSquare, GraduationCap, Video, Megaphone, Languages, Trophy, Palette, Bookmark, Brain, Tv, Atom, Zap, Vote, Calendar, PenTool, Search, Globe, MessageCircle, TrendingUp, Phone, Bot, ClipboardList } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/useLang";

const navGroups = [
  {
    labelKey: "nav.main",
    items: [
      { path: "/", labelKey: "nav.command", icon: Shield },
      { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
      { path: "/atlas-command", labelKey: "nav.atlasCommand", icon: Brain },
      { path: "/situation-room", labelKey: "nav.sitRoom", icon: BarChart3 },
      { path: "/ai-assistant", labelKey: "nav.aiAssistant", icon: MessageSquare },
      { path: "/smart-chat", labelKey: "nav.smartChat", icon: Sparkles },
    ],
  },
  {
    labelKey: "nav.training",
    items: [
      { path: "/floor-practice", labelKey: "nav.practice", icon: Dumbbell },
      { path: "/debate-forge", labelKey: "nav.forge", icon: BrainCircuit },
      { path: "/arena", labelKey: "nav.arena", icon: Swords },
      { path: "/gamification", labelKey: "nav.gamification", icon: Trophy },
    ],
  },
  {
    labelKey: "nav.coaching",
    items: [
      { path: "/floor-trainer", labelKey: "nav.floorTrainer", icon: Dumbbell },
      { path: "/coaching-dashboard", labelKey: "nav.coachingDashboard", icon: GraduationCap },
      { path: "/coach", labelKey: "nav.coach", icon: GraduationCap },
      { path: "/virtual-podium", labelKey: "nav.podium", icon: Mic },
      { path: "/lingual-studio", labelKey: "nav.lingual", icon: Languages },
      { path: "/profile", labelKey: "nav.profile", icon: User },
    ],
  },
  {
    labelKey: "nav.live",
    items: [
      { path: "/quick-launch", labelKey: "nav.quickLaunch", icon: Zap },
      { path: "/watch", labelKey: "nav.watchNow", icon: Tv },
      { path: "/virtual-meetings", labelKey: "nav.virtualMeetings", icon: Video },
      { path: "/virtual-meetings?tab=cabinet", labelKey: "nav.cabinetCouncil", icon: Landmark },
      { path: "/livestream", labelKey: "nav.livestream", icon: Radio },
      { path: "/live-assist", labelKey: "nav.liveAssist", icon: Headphones },
      { path: "/debate-lobby", labelKey: "nav.lobby", icon: Users },
      { path: "/debate-room", labelKey: "nav.debateRoom", icon: Video },
      { path: "/presidential-room", labelKey: "nav.presidentialRoom", icon: Landmark },
      { path: "/whatsapp", labelKey: "nav.floorCall", icon: Phone },
      { path: "/ceo-pilot", labelKey: "nav.ceoPilot", icon: Bot },
    ],
  },
  {
    labelKey: "nav.communityGroup",
    items: [
      { path: "/community", labelKey: "nav.community", icon: Users },
      { path: "/ai-poll", labelKey: "nav.aiPoll", icon: Vote },
      { path: "/ai-discourse-writer", labelKey: "nav.aiWriter", icon: PenTool },
      { path: "/scheduler", labelKey: "nav.scheduler", icon: Calendar },
      { path: "/bookmarks", labelKey: "nav.bookmarks", icon: Bookmark },
      { path: "/video-hub", labelKey: "nav.videoHub", icon: Grid },
      { path: "/creator", labelKey: "nav.creator", icon: Palette },
      { path: "/ads-manager", labelKey: "nav.adsManager", icon: Megaphone },
    ],
  },
  {
    labelKey: "nav.tools",
    items: [
      { path: "/comments", labelKey: "nav.comments", icon: MessageCircle },
      { path: "/polls", labelKey: "nav.polls", icon: BarChart3 },
      { path: "/chat-stats", labelKey: "nav.chatStats", icon: TrendingUp },
      { path: "/ai-tools", labelKey: "nav.aiTools", icon: Sparkles },
      { path: "/translate", labelKey: "nav.translate", icon: Globe },
      { path: "/ai-marketing", labelKey: "nav.aiMarketing", icon: Megaphone },
      { path: "/survey", labelKey: "nav.survey", icon: ClipboardList },
    ],
  },
  {
    labelKey: "nav.platform",
    items: [
      { path: "/sovereign", labelKey: "nav.sovereign", icon: Crown },
      { path: "/sovereign-enhanced", labelKey: "nav.sovereignAdvanced", icon: Atom },
      { path: "/sovereign-features", labelKey: "nav.features", icon: Sparkles },
      { path: "/pricing", labelKey: "nav.pricing", icon: Landmark },
      { path: "/trust-center", labelKey: "nav.trust", icon: ShieldCheck },
      { path: "/survey-results", labelKey: "nav.surveyResults", icon: BarChart3 },
    ],
  },
  {
    labelKey: "Admin",
    sovereignOnly: true,
    items: [
      { path: "/admin/users", labelKey: "User Management", icon: Users },
      { path: "/admin", labelKey: "Admin Console", icon: Shield },
    ],
  },
] as Array<{ labelKey: string; sovereignOnly?: boolean; items: Array<{ path: string; labelKey: string; icon: any }> }>;

// All searchable pages for the global search
const ALL_PAGES = navGroups.flatMap(g => g.items.map(i => ({ ...i, group: g.labelKey })));

const tierColors: Record<string, string> = {
  sovereign: "text-sovereign-gold",
  boardroom: "text-ai-cyan",
  academy: "text-tactical-green",
};

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tier, signOut } = useAuth();
  const { t } = useLang();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const initials = user?.email?.slice(0, 2).toUpperCase() || "U";

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("avatar_url").eq("id", user.id).single()
      .then(({ data }) => { if ((data as any)?.avatar_url) setAvatarUrl((data as any).avatar_url); });
  }, [user]);

  // Close search on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const filteredPages = searchQuery.trim()
    ? ALL_PAGES.filter(p =>
        t(p.labelKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.path.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : ALL_PAGES.slice(0, 8);

  const handleSearchSelect = (path: string) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery("");
  };

  return (
    <>
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl h-14 flex items-center px-4 md:px-6 gap-3">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-muted-foreground hover:text-foreground">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <Shield className="w-5 h-5 text-sovereign-gold" />
          <span className="font-bold text-sm tracking-widest uppercase text-gradient-gold hidden sm:inline">
            The Floor
          </span>
        </Link>

        <Link
          to="/"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            location.pathname === "/"
              ? "bg-sovereign-gold/15 text-sovereign-gold"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          <span className="hidden sm:inline">Overview</span>
        </Link>

        <Link
          to="/ai-poll"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            location.pathname === "/ai-poll"
              ? "bg-ai-cyan/15 text-ai-cyan"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          }`}
        >
          <Vote className="w-4 h-4" />
          <span className="hidden sm:inline">AI Polls</span>
        </Link>

        <Link
          to="/ceo-pilot"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            location.pathname === "/ceo-pilot"
              ? "bg-ai-cyan/15 text-ai-cyan"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          }`}
        >
          <Bot className="w-4 h-4" />
          <span className="hidden sm:inline">CEO Pilot</span>
        </Link>

        {/* Global Search */}
        <div ref={searchRef} className="relative flex-1 max-w-xs hidden md:block">
          <div
            onClick={() => { setSearchOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors cursor-text ${
              searchOpen ? "border-sovereign-gold/40 bg-secondary" : "border-border bg-secondary/50 hover:bg-secondary/80"
            }`}
          >
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            {searchOpen ? (
              <input
                ref={inputRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search pages..."
                className="bg-transparent text-xs text-foreground outline-none flex-1 placeholder:text-muted-foreground"
                onKeyDown={e => {
                  if (e.key === "Enter" && filteredPages.length > 0) {
                    handleSearchSelect(filteredPages[0].path);
                  }
                }}
              />
            ) : (
              <span className="text-xs text-muted-foreground flex-1">Search...</span>
            )}
            <kbd className="text-[9px] font-mono text-muted-foreground bg-background px-1.5 py-0.5 rounded border border-border">⌘K</kbd>
          </div>

          {/* Search Results Dropdown */}
          {searchOpen && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50 max-h-[320px] overflow-y-auto">
              {filteredPages.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">No results found</div>
              ) : (
                filteredPages.map(p => (
                  <button
                    key={p.path}
                    onClick={() => handleSearchSelect(p.path)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-secondary/50 transition-colors ${
                      location.pathname === p.path ? "bg-sovereign-gold/5 text-sovereign-gold" : ""
                    }`}
                  >
                    <p.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{t(p.labelKey)}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{p.path}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex-1 md:hidden" />

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mobile search button */}
          <button
            onClick={() => { setSearchOpen(!searchOpen); setTimeout(() => inputRef.current?.focus(), 50); }}
            className="md:hidden p-1.5 text-muted-foreground hover:text-foreground"
          >
            <Search className="w-4 h-4" />
          </button>

          <ThemeToggle />

          {user ? (
            <>
              {tier && (
                <span className={`text-[10px] font-mono uppercase tracking-widest ${tierColors[tier] || "text-muted-foreground"}`}>
                  {tier}
                </span>
              )}
              <NotificationBell />
              <Link to="/profile">
                <Avatar className="w-7 h-7 cursor-pointer border border-border hover:border-sovereign-gold/50 transition-colors">
                  <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
                  <AvatarFallback className="text-[10px] font-bold bg-secondary text-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <button onClick={signOut} className="text-muted-foreground hover:text-foreground transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/auth">
              <Button variant="outline-gold" size="sm" className="text-xs gap-1.5">
                <User className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("nav.signIn")}</span>
              </Button>
            </Link>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-tactical-green animate-pulse-gold" />
            <span className="text-xs font-mono text-muted-foreground hidden md:inline">{t("nav.secure")}</span>
          </div>
        </div>
      </nav>

      {/* Mobile search overlay */}
      {searchOpen && (
        <div className="fixed top-14 left-0 right-0 z-50 md:hidden bg-card border-b border-border p-3">
          <div className="flex items-center gap-2 bg-secondary rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search pages..."
              className="bg-transparent text-sm text-foreground outline-none flex-1"
              autoFocus
            />
            <button onClick={() => { setSearchOpen(false); setSearchQuery(""); }} className="text-muted-foreground"><X className="w-4 h-4" /></button>
          </div>
          {searchQuery.trim() && (
            <div className="mt-2 max-h-60 overflow-y-auto">
              {filteredPages.map(p => (
                <button
                  key={p.path}
                  onClick={() => handleSearchSelect(p.path)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary/50 rounded-lg"
                >
                  <p.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{t(p.labelKey)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sidebar */}
      <aside className={`fixed top-14 left-0 bottom-0 z-40 w-56 bg-sidebar-background border-r border-sidebar-border overflow-y-auto scrollbar-hide transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="py-4 px-3 space-y-5">
          {navGroups.filter(g => !g.sovereignOnly || tier === "sovereign").map((group) => (
            <div key={group.labelKey}>
              <p className={`text-[10px] font-mono font-semibold tracking-widest px-2 mb-1.5 ${group.sovereignOnly ? "text-sovereign-gold" : "text-muted-foreground"}`}>
                {t(group.labelKey)}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12px] font-medium tracking-wide transition-all ${
                        active
                          ? "bg-secondary text-sovereign-gold"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      }`}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      {t(item.labelKey)}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-background/60 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </>
  );
};

export default NavigationBar;
